import { Box } from "@mui/material";
import { DragEvent, PropsWithChildren, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = PropsWithChildren<{
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}>;

const ConversationDropZone = ({
  disabled = false,
  onFiles,
  children,
}: Props) => {
  const { t } = useTranslation("messaging");
  const [isDragging, setIsDragging] = useState(false);
  const dragCountRef = useRef(0);

  const hasFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || !hasFiles(event)) return;
    dragCountRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) return;
    dragCountRef.current = Math.max(0, dragCountRef.current - 1);
    if (dragCountRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCountRef.current = 0;
    setIsDragging(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {children}
      {isDragging && (
        <Box
          data-testid="conversation-drop-overlay"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(137, 95, 223, 0.08)",
            border: "2px dashed",
            borderColor: "primary.main",
            pointerEvents: "none",
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          {t("dropAttachments")}
        </Box>
      )}
    </Box>
  );
};

export default ConversationDropZone;
